import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Fragment } from 'react';

import { EditOrganizationUserForm } from '@/components/management/form/EditOrganizationUserForm';
import { AdministrationBody } from '@/components/management/layout/AdministrationBody';
import { AdministrationHeader } from '@/components/management/layout/AdministrationHeader';
import {
  backendApiOrganizationsFindOneOrganizationUserOptions,
  backendApiOrganizationsRemoveOrganizationUserMutation,
  backendApiOrganizationsUpdateOrganizationUserMutation,
} from '@/modules/api/backend/@tanstack/react-query.gen';
import { useConfirmStore } from '@/stores/useConfirmStore';
import { useSnackbarStore } from '@/stores/useSnackbarStore';

export const Route = createFileRoute(
  '/_authenticated/management/organizations/$organizationId/organizationUsers/$organizationUserId/',
)({
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: RouteComponent,
  beforeLoad: () => ({ breadcrumb: null }),
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const { organizationUserId, organizationId } = Route.useParams();

  const { data: organizationUser } = useSuspenseQuery(
    backendApiOrganizationsFindOneOrganizationUserOptions({
      path: { organizationId, userId: organizationUserId },
    }),
  );

  const { openSnackbar } = useSnackbarStore((state) => state.actions);
  const { openConfirm } = useConfirmStore((state) => state.actions);

  const { mutateAsync: mutateAsyncUpdate } = useMutation({
    ...backendApiOrganizationsUpdateOrganizationUserMutation(),
    onSuccess: () => {
      openSnackbar({
        color: 'success',
        message: 'Die Änderungen wurden erfolgreich gelöscht!',
      });
    },
    onError: () => {
      openSnackbar({
        color: 'error',
        message: 'Fehler beim Speichern.',
      });
    },
  });

  const { mutateAsync: mutateAsyncDelete } = useMutation({
    ...backendApiOrganizationsRemoveOrganizationUserMutation(),
    meta: {
      invalidateQueries: false,
    },
    onSuccess: () => {
      openSnackbar({
        color: 'success',
        message: 'Der Benutzer wurde erfolgreich entfernt!',
      });
      navigate({
        to: '/management/organizations/$organizationId',
        replace: true,
      });
    },
    onError: () =>
      openSnackbar({
        color: 'error',
        message: 'Fehler beim Löschen des Benutzers.',
      }),
  });

  return (
    <Fragment>
      <AdministrationHeader
        formId="organizationUserForm"
        label="User*in Bearbeiten"
        onDelete={async () => {
          const confirmed = await openConfirm({
            title: 'Benutzer entfernen',
            message: 'Sind Sie sicher, dass Sie diesen Benutzer aus der Organisation entfernen möchten?',
            confirmText: 'Entfernen',
            cancelText: 'Abbrechen',
          });

          if (confirmed) {
            await mutateAsyncDelete({
              path: { organizationId, userId: organizationUserId },
            });
          }
        }}
      />
      <AdministrationBody>
        <EditOrganizationUserForm
          initialValues={organizationUser}
          onSubmit={async (values) => {
            await mutateAsyncUpdate({ path: { organizationId, userId: organizationUserId }, body: values });
          }}
        />
      </AdministrationBody>
    </Fragment>
  );
}
